import React, { useEffect, useState, useCallback, useRef } from "react";

import PropTypes from "prop-types";

import { visuallyHidden } from "@mui/utils";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import AppBar from "@mui/material/AppBar";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";

import { ButtonUI } from "../../Common";
import { headCells, actionList, rowsPerPageOptions } from "./data";
import KMKDateRangePicker from "../../Common/KMKDateRangePicker";

import AuditService from "../../../service/auditServeice";

import { convertTimezone, setPrevState } from "../../../util/common";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import { dayjs } from "../../../util/dayjs";
import useWindowResize from "../../../hooks/useWindowsReszie";

const tableWidths = headCells.map((x) => x.width);
function DataTableHead(props) {
  const { order, orderBy, onRequestSort, hidden, classes } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead style={hidden ? { visibility: "collapse" } : {}}>
      <TableRow>
        {headCells.map(({ id, numeric, disablePadding, sortable, width, label, showTimezone }) => {
          return (
            <TableCell
              key={id}
              align={numeric ? "right" : "left"}
              padding={disablePadding ? "none" : "normal"}
              sortDirection={orderBy === id && !sortable ? order : false}
              style={{
                width: width,
              }}
              className={classes.TableHead}
            >
              {!sortable ? (
                label
              ) : (
                <TableSortLabel
                  active={orderBy === id}
                  direction={orderBy === id ? order : "asc"}
                  onClick={createSortHandler(id)}
                >
                  {label}
                  {orderBy === id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

DataTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const DataTableRow = ({ row, collapseOpen, setCollapseOpen }) => {
  const { classes } = useStyles();
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(false);

  const handleDetails = async (eventid) => {
    setLoading(true);
    const auditService = new AuditService();
    const response = await auditService.getEventDetails(eventid);
    if (response.success) {
      setDetails(response.data.details);
    }
    setLoading(false);
  };

  return (
    <>
      <TableRow
        style={{ cursor: "pointer" }}
        onClick={() => {
          setCollapseOpen((prev) => {
            return {
              id: row.Event_ID,
              open: row.Event_ID === prev.id ? !prev.open : true,
            };
          });
          if (Object.keys(details).length === 0) {
            handleDetails(row.Event_ID);
          }
        }}
        tabIndex={-1}
        key={row.Event_ID}
        hover={true}
        className={classes.tr}
      >
        <TableCell style={{ minWidth: tableWidths[0] }} component="td" scope="row" padding="normal">
          {row.Event_ID}
        </TableCell>
        <TableCell style={{ minWidth: tableWidths[1] }} component="td" scope="row" padding="normal">
          {row.EventDate ? convertTimezone(row.EventDate) : ""}
        </TableCell>
        <TableCell style={{ minWidth: tableWidths[2] }} component="td" scope="row" padding="normal">
          {row.EventType}
        </TableCell>
        <TableCell style={{ minWidth: tableWidths[3] }} component="td" scope="row" padding="normal">
          {row.User}
        </TableCell>

        <TableCell style={{ minWidth: tableWidths[4] }} component="td" scope="row" padding="normal">
          {row.Workspace}
        </TableCell>
        <TableCell style={{ minWidth: tableWidths[5] }} component="td" scope="row" padding="normal">
          {row.Message || ""}
        </TableCell>
      </TableRow>
      <TableRow style={{ backgroundColor: "#f5f5f5", maxWidth: "100%" }}>
        {loading ? (
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6} component="th">
            <Collapse in={collapseOpen.id === row.Event_ID && collapseOpen.open === true} timeout="auto">
              <Box
                sx={{
                  margin: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress size="30px" />
              </Box>
            </Collapse>
          </TableCell>
        ) : (
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6} component="th">
            <Collapse in={collapseOpen.id === row.Event_ID && collapseOpen.open === true} timeout="auto">
              <Box sx={{ margin: 1, maxWidth: "calc(100vw - 200px)" }}>
                <Typography variant="body2" gutterBottom component="div" fontWeight="bold">
                  Event Details:
                </Typography>
                <pre>{JSON.stringify(details, null, 2)}</pre>
              </Box>
            </Collapse>
          </TableCell>
        )}
      </TableRow>
    </>
  );
};

DataTableRow.propTypes = {
  row: PropTypes.object,
  collapseOpen: PropTypes.object,
  setCollapseOpen: PropTypes.func,
};

const useStyles = makeStyles()((theme) => ({
  tr: {
    "& td, & th": {
      wordBreak: "break-word",
    },
    "&:nth-of-type(odd)": {
      backgroundColor: "#ffffff",

      "& td:first-child": {
        backgroundColor: "#ffffff",
      },
    },
    "&:nth-of-type(even)": {
      "& td:first-child": {
        backgroundColor: "white",
      },
    },
  },
  pagination: {
    "& p": {
      marginBottom: "0px",
    },
  },
  Header: {
    fontSize: "1.1rem",
    // minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  TableHead: {
    fontWeight: 600,
    "& span.Mui-active": {
      background: "transparent",
      color: "inherit",
    },
    "& span": {
      "&:hover": {
        background: "transparent",
        color: "rgba(0, 0, 0, 0.6) !important",
      },
    },
  },
  AutoComplete: {
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap !important",
    },
    alignSelf: "flex-end",
    minWidth: "225px",
    maxWidth: "260px",
  },
  SelectChip: {
    maxWidth: "90px",
  },
  DateRangePicker: {
    maxWidth: "235px !important",
    borderRadius: "4px !important",
    padding: "8px 11px",
    backgroundColor: "transparent !important",
    borderColor: "rgba(0, 0, 0, 0.23)",
    "& .ant-picker-input>input": {
      fontSize: "0.85rem !important",
    },
    "& .ant-picker-suffix": {
      fontSize: "16px !important",
    },
    "& .ant-picker-clear": {
      fontSize: "16px !important",
      borderRadius: "10px",
    },
  },
}));

const CustomPaper = (props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
};

export default function DataTable() {
  const { classes } = useStyles();
  const { height: tableHeight } = useWindowResize(280);

  const [data, setData] = useState([]);
  const [collapseOpen, setCollapseOpen] = useState({
    id: null,
    open: false,
  });
  const [reportState, setReportState] = useState({
    collapse: false,
    dateRange: null,
    user: "",
    checkedTypes: [],
    limit: 25,
    count: 0,
    page: 0,
    orderBy: "EventDate",
    order: "desc",
    loading: false,
    selectedWorkspaces: [],
    workspaces: [],
  });
  const listingRef = useRef(reportState);

  const updateListingRefObj = () => {
    let { user, limit, page, orderBy, order, checkedTypes, dateRange, selectedWorkspaces } = reportState;
    selectedWorkspaces = selectedWorkspaces.map((workspace) => workspace.value);
    listingRef.current = { user, limit, page, orderBy, order, dateRange, checkedTypes, selectedWorkspaces };
  };

  const loadData = useCallback(async (options) => {
    setPrevState(setReportState, { loading: true });
    setCollapseOpen({
      id: null,
      open: false,
    });

    let startDate = listingRef.current.dateRange ? formatDate(listingRef.current.dateRange[0]) : -1;
    let endDate = listingRef.current.dateRange ? formatDate(listingRef.current.dateRange[1]) : -1;

    const auditService = new AuditService();
    const response = await auditService.getAuditReport({
      search: listingRef.current.user,
      limit: listingRef.current.limit,
      page: listingRef.current.page,
      orderBy: listingRef.current.orderBy,
      order: listingRef.current.order,
      eventTypes: listingRef.current.checkedTypes,
      selectedWorkspaces: listingRef.current.selectedWorkspaces,
      startDate,
      endDate,
      ...options,
    });

    if (response.success) {
      setData(response.data.reportList);
      setPrevState(setReportState, { count: response.data.count });
    }

    setPrevState(setReportState, { loading: false });
  }, []);

  const handleChangeRowsPerPage = (event) => {
    setPrevState(setReportState, { limit: parseInt(event.target.value, 10), page: 0 });
    updateListingRefObj();
    loadData({ limit: parseInt(event.target.value, 10), page: 0 });
  };

  const formatDate = (date) => date.format("YYYY-MM-DD");

  const handleApplyClick = () => {
    setPrevState(setReportState, { page: 0 });
    updateListingRefObj();
    loadData({ page: 0 });
  };

  const handleResetClick = () => {
    const date = dayjs().subtract(0, "day");
    const lastWeek = dayjs().subtract(7, "day");
    setPrevState(setReportState, {
      checkedTypes: [],
      user: "",
      dateRange: [lastWeek, date],
      selectedWorkspaces: [],
      page: 0,
    });
    updateListingRefObj();
    loadData({
      eventTypes: [1, 2, 3, 4, 5, 6, 99],
      search: "",
      startDate: formatDate(lastWeek),
      endDate: formatDate(date),
      selectedWorkspaces: [],
      page: 0,
    });
  };

  const handleRequestSort = (_event, property) => {
    const isAsc = reportState.orderBy === property && reportState.order === "asc";
    let tmpOrder = isAsc ? "desc" : "asc";

    setPrevState(setReportState, { order: tmpOrder, orderBy: property });
    updateListingRefObj();

    loadData({ order: tmpOrder, orderBy: property });
  };

  const handleChangePage = (_event, newPage) => {
    setPrevState(setReportState, { page: newPage });
    updateListingRefObj();

    loadData({ page: newPage });
  };

  const getAuditWorkspaces = async () => {
    const auditService = new AuditService();
    const response = await auditService.getAuditWorkspaces();
    setPrevState(setReportState, { workspaces: response.data });
  };

  useEffect(() => {
    let date = dayjs();
    const lastWeek = dayjs().subtract(7, "day");
    setPrevState(setReportState, { dateRange: [lastWeek, date] });
    loadData({ startDate: formatDate(lastWeek), endDate: formatDate(date) });
    getAuditWorkspaces();
  }, [loadData]);

  const disabledDate = (current) => current && current > dayjs().endOf("day");

  const DataTableToolbar = () => {
    return (
      <Box>
        <Box className={classes.Header} sx={{ display: "flex", alignItems: "center", paddingRight: "16px" }}>
          <Typography variant="h6" component="div">
            Audit Report
          </Typography>
          <HelpContextIcon screen="Audit Report" />
        </Box>
        <Box>
          <Box display="flex">
            <Box>
              <Box display="flex" alignItems="flex-start">
                <Box display={"flex"} flexWrap={"wrap"}>
                  <Box
                    sx={{
                      mr: "20px",
                      my: "5px",
                      alignSelf: "flex-end",
                    }}
                  >
                    <KMKDateRangePicker
                      format={(value) => value.format("MM/DD/YYYY")}
                      value={reportState.dateRange}
                      onChange={(value) => {
                        setPrevState(setReportState, { dateRange: value });
                        updateListingRefObj();
                      }}
                      className={classes.DateRangePicker}
                      disabledDate={disabledDate}
                      size="large"
                      separator="~"
                      allowClear={{ clearIcon: <ClearIcon sx={{ fontSize: "16px" }} /> }}
                      suffixIcon={
                        reportState.dateRange ? (
                          <ClearIcon sx={{ fontSize: "16px" }} />
                        ) : (
                          <DateRangeIcon sx={{ fontSize: "16px" }} />
                        )
                      }
                      variant="outlined"
                    />
                  </Box>
                  <Box>
                    <TextField
                      size="small"
                      label="User"
                      value={reportState.user}
                      sx={{ mr: "20px", minWidth: "225px", my: "5px" }}
                      onChange={(e) => {
                        setPrevState(setReportState, { user: e.target.value });
                        updateListingRefObj();
                      }}
                    />
                  </Box>
                  <Box>
                    <Autocomplete
                      multiple
                      fullWidth
                      PaperComponent={CustomPaper}
                      className={classes.AutoComplete}
                      sx={{ mr: "20px", my: "5px" }}
                      size="small"
                      options={reportState.workspaces}
                      value={reportState.selectedWorkspaces}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.name}
                      onChange={(e, selectedObj) => {
                        if (selectedObj.length) {
                          setPrevState(setReportState, {
                            selectedWorkspaces: [...selectedObj],
                          });
                        } else
                          setPrevState(setReportState, {
                            selectedWorkspaces: [],
                          });
                      }}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.value}>
                            {`${option.name} (${option.value})`}
                          </li>
                        );
                      }}
                      renderTags={(selected) => {
                        return selected.length <= 1 ? (
                          <Chip
                            label={`${selected[0].name} (${selected[0].value})`}
                            size="small"
                            className={classes.SelectChip}
                          />
                        ) : (
                          <>
                            <Chip
                              label={`${selected[0].name} (${selected[0].value})`}
                              size="small"
                              className={classes.SelectChip}
                            />
                            <p
                              style={{
                                fontSize: "0.8rem",
                                margin: "4px 0 0 7px",
                              }}
                            >{`+ ${selected.length - 1}`}</p>
                          </>
                        );
                      }}
                      renderInput={(params) => <TextField fullWidth variant="outlined" {...params} label="Workspace" />}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    my: "5px",
                    display: "flex",
                    flexWrap: "nowrap",
                  }}
                >
                  <IconButton color="secondary" onClick={handleResetClick}>
                    <Tooltip placement="top" title={"Reset"}>
                      <RestartAltIcon />
                    </Tooltip>
                  </IconButton>
                  <ButtonUI
                    variant="contained"
                    color="info"
                    sx={{
                      ml: "5px",
                    }}
                    onClick={handleApplyClick}
                  >
                    Apply
                  </ButtonUI>
                </Box>
              </Box>
              <Box>
                <FormControl>
                  <FormGroup
                    row
                    sx={{
                      ml: "10px",
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        color: "black",
                        "& .MuiTypography-root": {
                          fontWeight: "900",
                        },
                      }}
                      label="Event Type : "
                      control={<></>}
                    />
                    {actionList.map(({ name, value }) => (
                      <FormControlLabel
                        sx={{
                          color: "black",
                          "& .MuiTypography-root": {
                            fontSize: "0.9rem",
                          },
                        }}
                        key={value}
                        value={value}
                        control={
                          <Checkbox
                            sx={{
                              padding: "7px 4px 9px 9px",
                            }}
                            value={value}
                            size="small"
                            color="info"
                            checked={reportState.checkedTypes.includes(value)}
                          />
                        }
                        label={name}
                        labelPlacement="end"
                        onChange={(e) => {
                          setPrevState(setReportState, {
                            checkedTypes: e.target.checked
                              ? [...reportState.checkedTypes, value]
                              : reportState.checkedTypes.filter((val) => val !== value),
                          });
                          updateListingRefObj();
                        }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 6 }}>
      <AppBar
        sx={{
          position: "relative",
          boxShadow: 0,
          marginTop: "10px",
          backgroundColor: "#e8edf1",
        }}
      >
        {DataTableToolbar()}
      </AppBar>
      <Paper sx={{ width: "100%", mb: 2 }}>
        {reportState.loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: `${tableHeight}px`,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: `${tableHeight}px`,
              ...(data.length === 0
                ? {
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            <Table stickyHeader sx={{ width: "100%" }} size="small">
              <DataTableHead
                order={reportState.order}
                orderBy={reportState.orderBy}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                classes={classes}
              />
              {data.length ? (
                <TableBody>
                  {data.map((row) => (
                    <DataTableRow
                      key={row.Event_ID}
                      row={row}
                      collapseOpen={collapseOpen}
                      setCollapseOpen={setCollapseOpen}
                    />
                  ))}
                </TableBody>
              ) : (
                <TableBody></TableBody>
              )}
            </Table>
          </TableContainer>
        )}
        <>
          {!data.length && !reportState.loading && (
            <div
              style={{
                height: tableHeight - 64, //64 for the table title space
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              No records found
            </div>
          )}
        </>
        <TablePagination
          className={classes.pagination}
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={reportState.count}
          rowsPerPage={reportState.limit}
          page={reportState.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
